import React, { useState } from 'react';
import { generatePath, Link, useHistory } from 'react-router-dom';
import {
  Button,
  Checkbox,
  IconButton,
  LabelDisplayedRowsArgs,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { primaryColor, theme } from 'theme/theme';
import { useTranslation } from 'react-i18next';
import 'translations/translations';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import {
  LevelOfInvoiceEnumDto,
  ListOrganizationsDetailedResponseItem,
} from '.generated/api';
import clsx from 'clsx';
import { AuthRoutes } from 'shared/Routes';
import { FiberManualRecord } from '@material-ui/icons';
import { utcToLocalWithoutTz } from 'components/DateRangePicker';
import { ApiService } from 'services/apiService';
import { organizationActions } from 'actions/organizationActions';
import { useLocation } from 'react-router';
import { BulkUpdateMeasurementCardDialog } from './BulkUpdateMeasurementCardDialog';

const useStyles = makeStyles({
  tableLayout: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 18rem)',
    minHeight: '24rem',
    overflow: 'hidden',
  },
  tableContainer: {
    flex: 1,
    overflowY: 'auto',
  },
  table: {
    minWidth: '41rem',
    color: primaryColor(),
  },
  tableHead: {
    backgroundColor: 'rgba(240, 241, 243, 1)',
    color: primaryColor(),
  },
  tableHeadCell: {
    fontWeight: 'bold',
    color: primaryColor(),
    backgroundColor: 'rgba(240, 241, 243, 1)',
    zIndex: 2,
  },
  tableCell: {
    color: primaryColor(),
    position: 'relative',
  },
  tableRow: {
    textDecoration: 'none',
    cursor: 'pointer',
  },
  textBold: {
    fontWeight: 'bold',
    color: primaryColor(),
  },
  preDataView: {
    marginTop: '3rem',
    textAlign: 'center',
  },
  error: {
    color: theme.palette.error.main,
  },
  paddingLeft: {
    paddingLeft: '2rem',
  },
  statusIcon: {
    position: 'absolute',
    right: '100%',
    fontSize: '.85rem',
    marginRight: '-0.5rem',
    marginTop: '0.19rem',
  },
  pagination: {
    flexShrink: 0,
    borderTop: '1px solid rgba(224, 224, 224, 1)',
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
  },
  bulkToolbar: {
    flexShrink: 0,
    borderTop: '1px solid rgba(224, 224, 224, 1)',
    backgroundColor: 'rgba(240, 241, 243, 1)',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '0 1rem',
    minHeight: '3rem',
  },
  bulkToolbarText: {
    flexGrow: 1,
    color: primaryColor(),
  },
});

const getFormattedDate = (
  dateString: string | undefined,
): string | undefined => {
  const format = navigator.language;
  if (dateString) {
    const dateObject = utcToLocalWithoutTz(new Date(dateString));
    return dateObject.toLocaleDateString(format);
  } else {
    return undefined;
  }
};

const OrganizationsTable: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation(['translations']);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);

  const organizations = useSelector(
    (state: ApplicationState) => state.organizations?.organizations,
  );
  const filters = useSelector(
    (state: ApplicationState) => state.organizations?.filters,
  );

  const isGlobalAdmin = useSelector(
    (state: ApplicationState) =>
      state.isLoggedIn?.userAccess?.isGlobalAdmin ?? false,
  );

  const organizationsData = organizations?.data ?? [];

  const handleChangePage = (_: unknown, newPage: number) => {
    const query = new URLSearchParams(location.search);
    if (newPage === 0) {
      query.delete('page');
    } else {
      query.set('page', (newPage + 1).toString());
    }

    history.push({ search: query.toString() });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newPageSize = Number(event.target.value);
    const query = new URLSearchParams(location.search);
    query.delete('page');
    if (newPageSize === 25) {
      query.delete('pageSize');
    } else {
      query.set('pageSize', newPageSize.toString());
    }

    history.push({ search: query.toString() });
  };

  const getLevelOfInvoice = (levelOfInvoice?: LevelOfInvoiceEnumDto) => {
    switch (levelOfInvoice) {
      case LevelOfInvoiceEnumDto.ORGANIZATION:
        return t('labels.organization');
      case LevelOfInvoiceEnumDto.DEPARTMENT:
        return t('labels.department');
      default:
        return '';
    }
  };

  const handleDelete = async (
    org: ListOrganizationsDetailedResponseItem,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event?.stopPropagation();
    const confirmed = window.confirm(
      t('organizations_page.delete.confirm', {
        organization: org.organizationName,
      }),
    );
    if (confirmed) {
      const doubleConfirmed = window.confirm(
        t('organizations_page.delete.second_confirm', {
          organization: org.organizationName,
        }),
      );
      if (doubleConfirmed) {
        const reponse = await ApiService.deleteOrganization(org.organizationId);
        if (reponse.status !== 204) {
          alert(t('organizations_page.delete.error'));
          return;
        } else {
          alert(t('organizations_page.delete.success'));
        }
        dispatch(organizationActions.getOrganizations(filters));
      }
    }
  };

  const handleToggleSelect = (orgId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(orgId)) {
        next.delete(orgId);
      } else {
        next.add(orgId);
      }
      return next;
    });
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(new Set(organizationsData.map((o) => o.organizationId)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const selectedOrganizations = organizationsData
    .filter((o) => selectedIds.has(o.organizationId))
    .map((o) => ({ id: o.organizationId, name: o.organizationName ?? '' }));

  const allSelected =
    organizationsData.length > 0 &&
    organizationsData.every((o) => selectedIds.has(o.organizationId));
  const someSelected = selectedIds.size > 0 && !allSelected;

  return (
    <div className={classes.tableLayout}>
      <TableContainer className={classes.tableContainer}>
        <Table
          aria-label="organization table"
          className={classes.table}
          stickyHeader
        >
          <TableHead className={classes.tableHead}>
            <TableRow>
              {isGlobalAdmin && (
                <TableCell padding="checkbox" className={classes.tableHeadCell}>
                  <Checkbox
                    indeterminate={someSelected}
                    checked={allSelected}
                    onChange={handleSelectAll}
                    inputProps={{ 'aria-label': 'select all organisations' }}
                  />
                </TableCell>
              )}
              <TableCell
                className={clsx(classes.tableHeadCell, classes.paddingLeft)}
              >
                {t('organizations_page.table.name')}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {t('organizations_page.table.contact_person')}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {t('organizations_page.table.contact_no')}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {t('organizations_page.table.email')}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {t('organizations_page.table.level_of_invoice')}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {t('organizations_page.table.contract_expires')}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {t('organizations_page.table.measurement_card_name')}
              </TableCell>
              {isGlobalAdmin && (
                <TableCell className={classes.tableHeadCell}>
                  {t('organizations_page.table.state')}
                </TableCell>
              )}
              {isGlobalAdmin && (
                <TableCell className={classes.tableHeadCell}>{''}</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {organizationsData?.map(
              (org: ListOrganizationsDetailedResponseItem) => (
                <TableRow
                  key={org.organizationId}
                  hover={true}
                  selected={selectedIds.has(org.organizationId)}
                  onClick={() => {
                    history.push(`/organization/${org.organizationId}`);
                  }}
                  className={classes.tableRow}
                >
                  {isGlobalAdmin && (
                    <TableCell
                      padding="checkbox"
                      onClick={(e) => handleToggleSelect(org.organizationId, e)}
                    >
                      <Checkbox
                        checked={selectedIds.has(org.organizationId)}
                        inputProps={{
                          'aria-label': `select ${org.organizationName}`,
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell
                    className={clsx(classes.textBold, classes.paddingLeft)}
                  >
                    {org.organizationName}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {org.administrator}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {org.contactPhoneNumber}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {org.email}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {getLevelOfInvoice(org.contractInformation?.levelOfInvoice)}
                  </TableCell>
                  <TableCell
                    className={clsx(
                      classes.tableCell,
                      org.warnings.contractExpiresSoon && classes.error,
                    )}
                  >
                    {getFormattedDate(org.contractInformation?.expireDate)}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {org?.measurementCardName}
                  </TableCell>
                  {isGlobalAdmin && (
                    <TableCell className={classes.tableCell}>
                      <FiberManualRecord className={classes.statusIcon} />
                      {org.active
                        ? t('organizations_page.table.active')
                        : t('organizations_page.table.inactive')}
                      {org.warnings.isMissingCustomerNumber && (
                        <div className={classes.error}>
                          {t('organizations_page.table.customer_no_missing')}
                        </div>
                      )}
                      {org.warnings.departmentsWithMissingCustomerNumber
                        .length > 0 && (
                        <div className={classes.error}>
                          {`${t(
                            'organizations_page.table.customer_no_missing',
                          )}: `}
                          {org.warnings.departmentsWithMissingCustomerNumber.map(
                            (dep, i) => (
                              <Link
                                to={`${generatePath(AuthRoutes.Department, {
                                  orgId: org.organizationId,
                                  deptId: dep.departmentId,
                                })}?tab=1`}
                                key={dep.departmentId}
                                onClick={(event: React.MouseEvent) =>
                                  event.stopPropagation()
                                }
                              >
                                {dep.departmentName}
                                {i + 1 <
                                  org.warnings
                                    .departmentsWithMissingCustomerNumber
                                    .length && ', '}
                              </Link>
                            ),
                          )}
                        </div>
                      )}
                    </TableCell>
                  )}
                  {isGlobalAdmin && (
                    <TableCell className={classes.tableCell}>
                      <IconButton onClick={(event) => handleDelete(org, event)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ),
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {isGlobalAdmin && selectedIds.size > 0 && (
        <div className={classes.bulkToolbar}>
          <Typography variant="body2" className={classes.bulkToolbarText}>
            {t(
              'organizations_page.bulk_update_measurement_card.selected_count',
              {
                count: selectedIds.size,
              },
            )}
          </Typography>
          <Tooltip
            title={
              t(
                'organizations_page.bulk_update_measurement_card.action_button',
              ) as string
            }
          >
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              onClick={() => setBulkDialogOpen(true)}
            >
              {t(
                'organizations_page.bulk_update_measurement_card.action_button',
              )}
            </Button>
          </Tooltip>
          <Button size="small" onClick={() => setSelectedIds(new Set())}>
            {t('common.cancel')}
          </Button>
        </div>
      )}
      <TablePagination
        className={classes.pagination}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={organizations?.totalCount ?? 0}
        rowsPerPage={filters?.pageSize ?? 25}
        page={organizations?.page ? organizations.page - 1 : 0}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        backIconButtonText={t('pagination.previous_page')}
        labelDisplayedRows={(info: LabelDisplayedRowsArgs) => {
          return `${info.from}-${info.to} ${t('pagination.of')} ${info.count}`;
        }}
        labelRowsPerPage={t('pagination.rows_per_page') + ':'}
        nextIconButtonText={t('pagination.next_page')}
      />
      <BulkUpdateMeasurementCardDialog
        open={bulkDialogOpen}
        selectedOrganizations={selectedOrganizations}
        onClose={() => setBulkDialogOpen(false)}
        onSuccess={() => {
          setBulkDialogOpen(false);
          setSelectedIds(new Set());
        }}
      />
    </div>
  );
};

export { OrganizationsTable };
